import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { CalendarDays, Calculator, DollarSign, Percent, TrendingUp, AlertTriangle, Info, HelpCircle, X } from 'lucide-react'
import moment from 'moment'
import { toast } from 'react-toastify'

import { useSubscribers } from '../../../../../store/subscription/useSubscribers'
import { usePricing } from '../../../../../store/subscription/usePricing'
import { usePet } from '../../../../../store/global/usePet'
import { formatMoney } from '../../../../../services/functions'
import { SubscriberController } from '../../../../../services/controllers/subscription'

import { Input } from '../../../../../components/ui/Form/Input'
import { ToggleGroup } from '../../../../../components/ui/Form/ToggleGroup'
import { Action } from '../../../../../components/ui/Action'

// Importa estilos personalizados para altura dinâmica
import './styles.css'
import { useMerchant } from '../../../../../store/global/useMerchant'

export function UpdatePriceForm() {
  const details = useSubscribers(st => st.data.details)
  const pricingTable = usePricing(st => st.data.table)
  const CSubscriber = SubscriberController()
  const pet = usePet(st => st.data.pet)

  const storeId = useMerchant(st => st.data.merchant.id)

  // Estado do formulário
  const [adjustmentType, setAdjustmentType] = useState('value')
  const [currentPrice, setCurrentPrice] = useState(0)
  const [newValue, setNewValue] = useState(0)
  const [percentage, setPercentage] = useState(0)
  const [difference, setDifference] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showRulesModal, setShowRulesModal] = useState(false)
  const [planPrice, setPlanPrice] = useState(0)
  const [planType, setPlanType] = useState('')
  const [priceBanhoAvulso, setPriceBanhoAvulso] = useState(0)
  const [planFrequency, setPlanFrequency] = useState(0)
  const [currentPlan, setCurrentPlan] = useState({})
  const [createdAt, setCreatedAt] = useState('')
  const [status, setStatus] = useState('')
  const [nextBillingAt, setNextBillingAt] = useState('')
  const [pendingPriceAdjustment, setPendingPriceAdjustment] = useState([])
  const [validationErrors, setValidationErrors] = useState([])
  const [cyclesPaid, setCyclesPaid] = useState([])

  useEffect(() => {
    // Verifica se os detalhes estão carregados antes de acessar suas propriedades
    if (!details || !details.plan) {
      // Limpa todos os estados quando não há detalhes
      setPercentage(0)
      setDifference(0)
      setAdjustmentType('value')
      setShowRulesModal(false)
      setCurrentPlan({})
      setCreatedAt('')
      setStatus('')
      setNextBillingAt('')
      setPendingPriceAdjustment([])
      setPlanPrice(0)
      setPlanType('')
      setPriceBanhoAvulso(0)
      setPlanFrequency(0)
      setCurrentPrice(0)
      setNewValue(0)
      setValidationErrors([])
      setCycles([])
      return
    }

    // Reseta todos os estados do formulário quando os detalhes mudam
    setPercentage(0)
    setDifference(0)
    setAdjustmentType('value')
    setShowRulesModal(false)

    // Atualiza os dados da assinatura atual
    setCurrentPlan(details?.plan || {})
    setCyclesPaid(details?.cycles?.filter(cycle => cycle.status.text === 'Pago').sort((a, b) => {
      const dateA = moment(a.billingAt, 'DD/MM/YYYY')
      const dateB = moment(b.billingAt, 'DD/MM/YYYY')
      return dateB.valueOf() - dateA.valueOf() // Ordena do mais recente para o mais antigo
    }).slice(0, 3) || [])
    setCreatedAt(details?.createdAt || '')
    setStatus(details?.status || '')
    setNextBillingAt(details?.nextBillingAt || '')
    setValidationErrors([])

    // Atualiza reajustes pendentes - filtra corretamente por jobName e jobStatus
    const pendingAdjustments = details?.queueJobs?.filter(job =>
      job?.jobName === 'Ajuste preço assinatura' &&
      job?.jobStatus === 'Pendente'
    ) || []

    console.log('Reajustes pendentes encontrados:', pendingAdjustments)
    setPendingPriceAdjustment(pendingAdjustments)

    // Atualiza preços da tabela de preços
    const size = pet?.petAttributes?.size
    const planType = details?.plan?.type
    const planData = pricingTable?.[size]?.find(p => p?.plano === planType)

    setPlanPrice(planData?.['precoAssinatura'] || 0)
    setPlanType(planType || '')
    setPriceBanhoAvulso(planData?.['precoBanho'] || 0)

    // Define frequência baseada no tipo de plano
    if (planType === 'Básico') {
      setPlanFrequency(3)
    } else if (planType === 'Essencial') {
      setPlanFrequency(4)
    } else if (planType === 'Ilimitado') {
      setPlanFrequency(5)
    } else if (planType === 'Black') {
      setPlanFrequency(5)
    } else {
      setPlanFrequency(0)
    }
  }, [details?.id, details?.plan, details?.queueJobs, pet?.petAttributes?.size, pricingTable])

  // useEffect separado para definir o preço atual e novo valor
  useEffect(() => {
    const price = currentPlan?.price || 0
    setCurrentPrice(price)
    setNewValue(price)
  }, [currentPlan])

  // Validações de regras de negócio
  const rulesValidationErrors = useMemo(() => {
    const errors = []

    // Verifica se há reajuste pendente - usa o estado atualizado
    if (pendingPriceAdjustment?.length > 0) {
      errors.push('Já existe um reajuste pendente')
    }

    // // Verifica se há pelo menos 3 ciclos pagos
    // if (cyclesPaid.length < 3) {
    //   errors.push(
    //     `Reajuste só pode ser aplicado após a 3ª cobrança. Atualmente há ${cyclesPaid.length} cobrança(s) paga(s).`
    //   )
    // }

    // // Verifica se os 3 últimos ciclos têm o mesmo preço
    // const firstPrice = cyclesPaid[0]?.paidAmount || 0
    // const allSamePrice = cyclesPaid?.every(cycle => cycle?.paidAmount === firstPrice)

    // if (!allSamePrice) {
    //   errors.push('Reajuste só pode ser aplicado após 3 cobranças com o mesmo valor. Os últimos 3 pagamentos têm valores diferentes.')
    // }

    // Verificar se status é cancelado
    const statusUpper = String(status).toUpperCase()
    if (statusUpper === 'CANCELADO' || statusUpper === 'CANCELED') {
      errors.push('Assinatura deve estar ativa para aplicar reajuste')
    }

    return errors
  }, [createdAt, details?.id, details?.priceAdjustment, status, pendingPriceAdjustment])

  // Atualiza o estado de erros quando as regras mudam
  useEffect(() => {
    setValidationErrors(rulesValidationErrors)
  }, [rulesValidationErrors])

  // Cálculos automáticos
  const calculatedValues = useMemo(() => {
    let finalPrice = 0
    let calculatedPercentage = 0
    let calculatedDifference = 0

    switch (adjustmentType) {
      case 'value':
        // Usuário define o novo valor total
        finalPrice = newValue
        calculatedDifference = newValue - currentPrice
        calculatedPercentage = currentPrice > 0 ? ((newValue - currentPrice) / currentPrice) * 100 : 0
        break
      case 'percentage':
        // Usuário define o percentual de aumento
        calculatedPercentage = percentage
        calculatedDifference = currentPrice * (percentage / 100)
        finalPrice = currentPrice + calculatedDifference
        break
      case 'difference':
        // Usuário define a diferença em reais
        calculatedDifference = difference
        finalPrice = currentPrice + difference
        calculatedPercentage = currentPrice > 0 ? (difference / currentPrice) * 100 : 0
        break
      default:
        finalPrice = currentPrice
        calculatedDifference = 0
        calculatedPercentage = 0
    }

    // Validações adicionais
    const validationErrors = []

    // Verificar limites de reajuste (aumento máximo de 20% ou redução máxima de 50%)

    // if (!["5e737294466ef5256b6dd780", "64ecf92ab7f4f341d8496670", "68b829b4078ec20d4314e0ff", "63aaff414907131207488e0b", "6000722dcf1e3b375688d07d"].includes(storeId)) {
    //   if (calculatedPercentage > 20) {
    //     validationErrors.push('Reajuste não pode ultrapassar 20%')
    //   }

    //   //Valor não pode superar o valor da faixa atual
    //   if (planPrice > 0 && finalPrice > planPrice) {
    //     validationErrors.push('O novo valor não pode superar o valor da faixa atual, Faixa atual: R$ ' + formatMoney(planPrice, 2))
    //   }
    //   //Valor não pode superar o valor de referência de um banho avulso (3 basico, 4 essencial e 5 ilimitado)
    //   if (planFrequency > 0 && priceBanhoAvulso > 0 && (finalPrice / planFrequency) > priceBanhoAvulso) {
    //     validationErrors.push('O novo valor não pode superar o valor de referência de um banho avulso, Valor Banho Avulso: R$ ' + formatMoney(priceBanhoAvulso, 2))
    //   }
    // }

    return {
      finalPrice: Math.max(0, finalPrice),
      calculatedPercentage,
      calculatedDifference,
      validationErrors
    }
  }, [adjustmentType, newValue, percentage, difference, currentPrice, planPrice, planFrequency, priceBanhoAvulso])

  const isValid = validationErrors.length === 0 && calculatedValues.validationErrors.length === 0

  const handleSubmit = useCallback(async () => {
    if (!isValid || isSubmitting || !details?.id) return

    setIsSubmitting(true)

    try {
      const adjustmentData = {
        subscriptionId: details.id, // Adicionando o ID da assinatura
        type: adjustmentType,
        currentPrice,
        newPrice: calculatedValues.finalPrice,
        percentage: calculatedValues.calculatedPercentage,
        difference: calculatedValues.calculatedDifference
      }

      const success = await CSubscriber.priceAdjustment(adjustmentData)

      if (success) {
        // Limpar formulário após sucesso
        handleReset()
        // Os detalhes já são atualizados automaticamente pelo priceAdjustment
        // O useEffect será disparado quando details.queueJobs mudar
      }
    } catch (error) {
      console.error('Erro ao aplicar reajuste:', error)
      toast.error('Erro ao aplicar reajuste. Tente novamente.')
    } finally {
      setIsSubmitting(false)
    }
  }, [isValid, isSubmitting, adjustmentType, currentPrice, calculatedValues, CSubscriber, details?.id, pet?.id])

  const handleReset = useCallback(() => {
    setNewValue(0)
    setPercentage(0)
    setDifference(0)
  }, [])

  // Verifica se os detalhes estão carregados antes de renderizar
  if (!details) {
    return null
  }

  return (
    <div className="flex flex-col gap-6 p-4 subscription-price-update-dynamic">
      {/* Modal de regras */}
      {showRulesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800 flex items-center">
                <Info className="w-5 h-5 mr-2 text-blue-600" />
                Regras para Reajuste de Assinatura
              </h3>
              <button
                onClick={() => setShowRulesModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="text-sm text-gray-700 space-y-4">
              <div>
                <p className="font-semibold text-blue-800 mb-2">✅ Critérios de Elegibilidade:</p>
                <ul className="ml-4 space-y-1">
                  <li>• Assinatura deve estar ativa há mais de 3 meses.</li>
                  <li>• Cliente não pode ter sofrido reajuste nos últimos 3 meses.</li>
                  <li>• Novo valor não pode superar valor de banho avulso, Valor do plano / Base de Frequência. (Black/Ilimitado: 5, Essencial: 4, Básico: 3)</li>
                  <li>• Novo valor não pode superar valor da faixa atual.</li>
                </ul>
              </div>

              <div>
                <p className="font-semibold text-blue-800 mb-2">🗓️ Aplicação:</p>
                <p>O novo preço aparece na segunda fatura com vencimento após a criação do reajuste. Vencimentos no mesmo dia não contam. Editar o valor reinicia a contagem.</p>
              </div>

              <div className="bg-gray-50 p-3 rounded">
                <p className="font-semibold text-blue-800 mb-1">💡 Exemplo:</p>
                <p>
                  Reajuste criado em 11/07: fatura com vencimento em 16/07 mantém o valor; fatura de 16/08 recebe o novo preço.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={() => setShowRulesModal(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                Entendi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Informações atuais da assinatura */}
      <div className="border rounded-md p-4 bg-gray-50">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-700 flex items-center">
            <CalendarDays className="w-5 h-5 mr-2" />
            Informações Atuais da Assinatura
          </h3>
          <button
            onClick={() => setShowRulesModal(true)}
            className={`flex items-center justify-center w-8 h-8 rounded-full transition-all duration-300 ${validationErrors.length > 0 || calculatedValues.validationErrors.length > 0
              ? 'bg-red-100 text-red-600 animate-pulse hover:animate-bounce border-2 border-red-300'
              : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
              }`}
            title="Ver regras para reajuste"
          >
            <HelpCircle className="w-5 h-5" />
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">Plano atual:</span>
              <span className="ml-2 font-bold text-gray-800">{currentPlan?.type || 'N/A'}</span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">Valor atual:</span>
              <span className="ml-2 font-bold text-green-600">
                R$ {formatMoney(currentPrice, 2)}
              </span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">Próxima cobrança:</span>
              <span className="ml-2 font-medium text-gray-800">{nextBillingAt}</span>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">Data da contratação:</span>
              <span className="ml-2 font-medium text-gray-800">{createdAt}</span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">Status:</span>
              <span className={`ml-2 px-2 py-1 rounded text-xs font-medium ${status === 'active' || status === 'Ativo' ? 'bg-green-100 text-green-800' :
                status === 'paused' || status === 'Pausado' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                {status === 'active' ? 'Ativo' :
                  status === 'paused' ? 'Pausado' :
                    status === 'canceled' ? 'Cancelado' : status}
              </span>
            </div>
            <div className="flex items-center">
              <span className="text-gray-600 font-medium">O novo preço aparecerá na segunda fatura com vencimento após a criação do reajuste. Vencimentos no mesmo dia não contam.</span>
            </div>
          </div>
        </div>
      </div>


      {/* Seleção do tipo de reajuste */}
      <div className="border rounded-md p-4">
        <h3 className="text-lg font-semibold text-gray-700 mb-4 flex items-center">
          <Calculator className="w-5 h-5 mr-2" />
          Tipo de Reajuste
        </h3>

        <ToggleGroup.Root
          value={adjustmentType}
          onValueChange={setAdjustmentType}
          type="single"
          className="mb-6"
          disabled={isSubmitting}
        >
          <ToggleGroup.Item value="value">
            <DollarSign className="w-4 h-4 mr-2" />
            Novo Valor Total
          </ToggleGroup.Item>
          <ToggleGroup.Item value="percentage">
            <Percent className="w-4 h-4 mr-2" />
            Percentual de Aumento
          </ToggleGroup.Item>
          <ToggleGroup.Item value="difference">
            <TrendingUp className="w-4 h-4 mr-2" />
            Diferença de Preço
          </ToggleGroup.Item>
        </ToggleGroup.Root>

        {/* Campos condicionais baseados no tipo selecionado */}
        <div className="space-y-4">
          {adjustmentType === 'value' && (
            <div className="max-w-xs">
              <Input.Root
                label="Novo valor da assinatura"
                money
                decimals={2}
                value={newValue}
                onChange={setNewValue}
                disabled={isSubmitting}
              />
            </div>
          )}

          {adjustmentType === 'percentage' && (
            <div className="max-w-xs">
              <Input.Root
                label="Percentual de aumento"
                number
                decimals={2}
                suffix="%"
                value={percentage}
                onChange={setPercentage}
                disabled={isSubmitting}
              />
            </div>
          )}

          {adjustmentType === 'difference' && (
            <div className="max-w-xs">
              <Input.Root
                label="Valor do aumento"
                money
                decimals={2}
                value={difference}
                onChange={setDifference}
                disabled={isSubmitting}
              />
            </div>
          )}
        </div>
      </div>

      {/* Alertas de validação */}
      {(validationErrors.length > 0 || calculatedValues.validationErrors.length > 0) && (
        <div className="border rounded-md p-4 bg-red-50 border-red-200" style={{
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          overflow: 'visible',
          minWidth: 0,
          width: '100%'
        }}>
          <h3 className="text-lg font-semibold text-red-800 mb-3 flex items-center">
            <AlertTriangle className="w-5 h-5 mr-2 flex-shrink-0" />
            Problemas Encontrados
          </h3>
          <div className="text-sm text-red-700 space-y-2" style={{ width: '100%' }}>
            {[...validationErrors, ...calculatedValues.validationErrors].map((error, index) => (
              <p key={index} style={{
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                lineHeight: '1.5',
                width: '100%',
                overflow: 'visible'
              }}>
                • {error}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* Resumo dos cálculos */}
      <div className="border rounded-md p-4 bg-orange-50">
        <h3 className="text-lg font-semibold text-gray-700 mb-4">Resumo do Reajuste</h3>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="text-center">
            <div className="text-sm text-gray-600">Valor Atual</div>
            <div className="text-xl font-bold text-gray-800">
              R$ {formatMoney(currentPrice, 2)}
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-600">Novo Valor</div>
            <div className={`text-xl font-bold ${calculatedValues.finalPrice >= currentPrice ? 'text-green-600' : 'text-red-600'
              }`}>
              R$ {formatMoney(calculatedValues.finalPrice, 2)}
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-600">Diferença</div>
            <div className={`text-xl font-bold ${calculatedValues.calculatedDifference >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
              R$ {formatMoney(calculatedValues.calculatedDifference, 2)}
            </div>
          </div>

          <div className="text-center">
            <div className="text-sm text-gray-600">Percentual</div>
            <div className={`text-xl font-bold ${calculatedValues.calculatedPercentage >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
              {calculatedValues.calculatedPercentage.toFixed(2)}%
            </div>
          </div>
        </div>
      </div>

      {/* Botões de ação */}
      <div className="flex justify-between items-center">
        <Action.Root
          outline
          status="secondary"
          onClick={handleReset}
          disabled={isSubmitting}
        >
          Limpar Campos
        </Action.Root>

        <div className="flex gap-3">
          <Action.Root
            outline
            status="danger"
            disabled={isSubmitting}
          >
            Cancelar
          </Action.Root>

          <Action.Root
            status="success"
            onClick={handleSubmit}
            disabled={!isValid || calculatedValues.finalPrice <= 0 || isSubmitting}
          >
            {isSubmitting ? 'Aplicando...' : 'Solicitar Reajuste'}
          </Action.Root>
        </div>
      </div>
    </div>
  )
}
